import type { ApiProblem, HoComponents } from '@pos/api-client';
import { hoApi } from '../../api';

type Dto = HoComponents['schemas'];
export type ImportReport = Dto['ImportReportDto'];
export type ImportSheetReport = Dto['ImportSheetReportDto'];
export type ImportProblem = Dto['ImportProblemDto'];

/** The workbook's sheets, in the order the import reads them. */
export const IMPORT_SHEETS = [
  'categories',
  'items',
  'units',
  'option_groups',
  'options',
  'item_options',
  'deals',
  'deal_components',
  'deal_choices',
  'menus',
  'layouts',
  'price_lists',
  'prices',
  'menu_items',
  'buttons',
] as const;

/** Any one lets a user import; the server checks each non-empty sheet against its own permission. */
export const IMPORT_MANAGE = [
  'ho.catalog.categories.manage',
  'ho.catalog.items.manage',
  'ho.catalog.modifier_groups.manage',
  'ho.catalog.deals.manage',
  'ho.menu.menus.manage',
  'ho.menu.layouts.manage',
  'ho.menu.price_lists.manage',
].join('|');

/** Any one lets a user download the workbook (sheets they can't read come empty). */
export const IMPORT_READ = IMPORT_MANAGE.replaceAll('.manage', '.read');

/** Dates in the workbook are days in the browser's time zone, as on the price table. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The file name from a Content-Disposition header (RFC 6266, `filename*` first). */
export function dispositionFileName(header: string | null | undefined): string | undefined {
  if (!header) return undefined;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // Falls back to the plain name.
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain?.[1]?.trim() || undefined;
}

/** The brand's current data as the import workbook, made into a file in the browser (no link with a token). */
export async function downloadWorkbook(brandId: string): Promise<{ blob: Blob; fileName?: string }> {
  const { data, response } = (await hoApi.GET('/api/v1/menu/import/workbook', {
    params: { query: { brandId, timeZone: browserTimeZone() } },
    parseAs: 'blob',
  } as never)) as { data?: unknown; response: Response };
  if (!response.ok || !(data instanceof Blob)) throw new Error(String(response.status));
  return { blob: data, fileName: dispositionFileName(response.headers.get('Content-Disposition')) };
}

async function send(path: '/api/v1/menu/import/preview' | '/api/v1/menu/import', brandId: string, file: File) {
  const body = new FormData();
  body.append('file', file, file.name);
  body.append('brandId', brandId);
  body.append('timeZone', browserTimeZone());
  // openapi-fetch sends FormData as it is and lets the browser set the multipart boundary.
  const { data, error } = await hoApi.POST(path, { body: body as never });
  if (error || !data) throw (error ?? {}) as ApiProblem;
  return data as ImportReport;
}

/** What importing the workbook would do; nothing is written. */
export const previewImport = (brandId: string, file: File) => send('/api/v1/menu/import/preview', brandId, file);

/** Imports the workbook in one transaction; with any problem nothing is written (`applied: false`). */
export const applyImport = (brandId: string, file: File) => send('/api/v1/menu/import', brandId, file);
